'use strict';

// Import Libraries
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import PropTypes from 'prop-types';
import { href, link_classes } from './utils';
import { builderDispatch, WebUI } from '../_utils/ui';

const DEFAULT_ORDER_STATE = Object.freeze({
  storage_provider_present: false,
  logged_in_user_name: '',
});

const MobileSideMenu = ({ headerLinks, links, orderProp }) => {
  useEffect(() => {
    const builderDispatchLogin = builderDispatch.register((event) => {
      switch (event.type) {
        case WebUI.LOGIN:
          updateComponent();
          break;
      }
    });
    return () => {
      builderDispatch.unregister(builderDispatchLogin);
    };
  });

  const initialOrder = orderProp || DEFAULT_ORDER_STATE;

  const [order, setOrder] = useState(initialOrder);

  const [loggedIn, setLoggedIn] = useState(
    Boolean(initialOrder.logged_in_user_name),
  );

  useEffect(() => {
    const appOrder =
      typeof window !== 'undefined' ? window.App?.order : undefined;
    if (appOrder) {
      setOrder(appOrder);
      setLoggedIn(Boolean(appOrder.logged_in_user_name));
    } else if (typeof window !== 'undefined' && window.initialOrderProps) {
      setOrder(window.initialOrderProps);
      setLoggedIn(Boolean(window.initialOrderProps.logged_in_user_name));
    } else {
      setOrder(DEFAULT_ORDER_STATE);
      setLoggedIn(false);
    }
  }, []);

  const [values, setValues] = useState({
    opened: false,
    display: 'none',
    update: false,
  });

  const updateComponent = () => {
    setValues({ ...values, update: !values.update });
    const appOrder =
      typeof window !== 'undefined'
        ? window.App?.order || window.initialOrderProps
        : undefined;
    if (appOrder) {
      setOrder(appOrder);
      setLoggedIn(Boolean(appOrder.logged_in_user_name));
    } else {
      setOrder(DEFAULT_ORDER_STATE);
      setLoggedIn(false);
    }
  };

  const renderAccountTools = () => {
    const { store_finder: storeFinderUrl, help_page: helpPageUrl } = headerLinks;
    const utilityLinks = () => {
      if (!storeFinderUrl && !helpPageUrl) {
        return null;
      }

      return (
        <>
          <li className="sfinder">
            <a href={storeFinderUrl} role="button">
              <i className="skinny-icon pin"></i>Store Finder
            </a>
          </li>
          <li className="help">
            <a href={helpPageUrl} role="button">
              <i className="skinny-icon help"></i>Help
            </a>
          </li>
        </>
      );
    };

    const renderHeaderLinks = () => {
      if (loggedIn) {
        const {
          logged_in_links: {
            order_prints: orderPrintsUrl,
            user_albums: userAlbumsUrl,
          },
        } = headerLinks;

        const uploadLinks = () => {
          const storageProviderPresent = Boolean(
            order?.storage_provider_present,
          );
          if (!storageProviderPresent) {
            return null;
          }

          return (
            <>
              <li className="upload">
                <a href={orderPrintsUrl} role="button">
                  <i className="skinny-icon upload"></i>Upload Photos
                </a>
              </li>
              <li className="photos">
                <a href={userAlbumsUrl} role="button">
                  <i className="skinny-icon photos"></i>My Photos
                </a>
              </li>
            </>
          );
        };
        const userName = order?.logged_in_user_name;

        const {
          logged_in_links: {
            user_projects: userProjectsUrl,
            user_account: userAccountUrl,
            track_orders: trackOrdersUrl,
            logout: logoutUrl,
          },
        } = headerLinks;

        return (
          <>
            <span className="logged-in">Hello, {userName}</span>
            <ul>
              {uploadLinks()}
              <li className="projects">
                <a href={userProjectsUrl} role="button">
                  <i className="skinny-icon projects"></i>My Projects
                </a>
              </li>
              <li className="account">
                <a href={userAccountUrl} role="button">
                  <i className="skinny-icon account"></i>My Account
                </a>
              </li>
              <li className="orders">
                <a href={trackOrdersUrl} role="button">
                  <i className="skinny-icon orders"></i>Track Orders
                </a>
              </li>
              {utilityLinks()}
              <li className="sign-out">
                <a href={logoutUrl} role="button">
                  <i className="skinny-icon sign-out"></i>Sign Out
                </a>
              </li>
            </ul>
          </>
        );
      } else {
        const { new_account: newAccountUrl, store_name: storeName } = headerLinks;

        return (
          <>
            <span>Account</span>
            <ul>
              <li className="sign-in">
                <a href="#" data-login="" role="button">
                  <i className="skinny-icon anna sign-in"></i>Sign In
                </a>
              </li>
              <li className="create-account">
                <a href={newAccountUrl} role="button">
                  <i className="skinny-icon create-account"></i>Create Account
                  <small className="d-block">Get more out of {storeName}</small>
                </a>
              </li>
              {utilityLinks()}
            </ul>
          </>
        );
      }
    };

    return (
      <li id="login-status-burger" className="category">
        {renderHeaderLinks()}
      </li>
    );
  };

  return (
    <>
      <FocusTrap active={values.opened} focusTrapOptions={{ returnFocusOnDeactivate: true }}>
        <div>
          <button
            className="close"
            id="close-menu-button"
            data-target="#close-sidrs"
            type="button"
            aria-label="Close">
            <i className="skinny-icon close" />
          </button>
          <ul>
            {renderAccountTools()}
            <li className="category border-top">
              <span>Categories</span>
              <ul>
                {links.map((link, i) => {
                  return (
                    <li key={i} className={classNames(link_classes(link))}>
                      <a href={href(link)} role="button">
                        {link.name}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </li>
          </ul>
        </div>
      </FocusTrap>
    </>
  );
};

MobileSideMenu.propTypes = {
  headerLinks: PropTypes.object,
  links: PropTypes.array,
  orderProp: PropTypes.object,
};

MobileSideMenu.defaultTypes = {
  headerLinks: null,
  links: [],
  orderProp: null,
};

export default MobileSideMenu;
